import "#lib/assert.js";
import "#/legacy/namespaces.js";
import "#legacy/shared/components/tooltips.js";
import "#legacy/shared/events.js";

DoctorFinder.Views.Base = class Base extends Backbone.View {
  // CLASS ATTRIBUTES

  constructor() {
    super(...arguments);
  }

  static addMobileBreakpointHandler(enterHandler, exitHandler) {
    if (!DoctorFinder.Views.Base.__mediaCheck) {
      DoctorFinder.Views.Base.__mediaCheck = new Sparkle.Responsive.mediaCheck({
        media: "(max-width:" + Sparkle.Responsive.Entities.lgMobile + "px)",
        entry() {
          return (DoctorFinder.Views.Base.isMobileSize = true);
        },
        exit() {
          return (DoctorFinder.Views.Base.isMobileSize = false);
        },
      });
    }

    const handler = (match) => {
      try {
        if (match) {
          return enterHandler();
        } else {
          return exitHandler();
        }
      } catch (e) {
        // Handler may fail during construction — will be re-evaluated on next media change
        if (!(e instanceof TypeError)) {
          throw e;
        }
      }
    };

    // Evaluate the handler for the current state
    handler(DoctorFinder.Views.Base.__mediaCheck.matches);

    // Use the handler for future state changes
    return DoctorFinder.Views.Base.__mediaCheck.addListener(handler);
  }

  preinitialize() {
    super.preinitialize();

    this.eachAncestor((ancestorProtoype) => {
      // Inherit events from superclass
      this.events = {
        ...ancestorProtoype.events,
        ...this.events,
      };

      // Inherit class names from superclass
      const ancestorClassName = ancestorProtoype.className;
      if (ancestorClassName && ancestorClassName !== this.className) {
        return (this.className = jQuery.trim(`${ancestorClassName} ${this.className}`));
      }
    });

    // Make it easy for views to assert things
    this.assert = new Asserter({ notifyUser: false });

    // Ensure a template is defined
    if (this.template !== false) {
      return this.assert.presence(this.template, `No template defined for ${this.constructor.name}`);
    }
  }

  initialize(options) {
    this.preinitialize = this.preinitialize.bind(this);
    this.initialize = this.initialize.bind(this);
    this.render = this.render.bind(this);
    this.rerender = this.rerender.bind(this);
    this.renderTemplate = this.renderTemplate.bind(this);
    this.contextForRender = this.contextForRender.bind(this);
    this.i18nForRender = this.i18nForRender.bind(this);
    this.getStickyHeaderOffset = this.getStickyHeaderOffset.bind(this);
    this.scrollToTop = this.scrollToTop.bind(this);
    this.eachAncestor = this.eachAncestor.bind(this);
    this.renderPartial = this.renderPartial.bind(this);
    this.replaceContents = this.replaceContents.bind(this);
    this.proxyEvent = this.proxyEvent.bind(this);
    this.triggerEvent = this.triggerEvent.bind(this);

    if (options == null) {
      options = {};
    }
    this.I18n = new Sparkle.I18n(this.I18n).merge(options.i18n); // Extend the I18n defined in the code with any additional keys passed in to the constructor

    this.options = _.defaults(options, {
      stickyHeaderOffset: undefined, // Offset to add to sticky elements in case other fixed-position elements exist
    });

    super.initialize(this.options);

    // BEHAVIOUR

    const basePrototype = DoctorFinder.Views.Base.prototype;
    if (
      this.handleMobileBreakpointEnter !== basePrototype.handleMobileBreakpointEnter ||
      this.handleMobileBreakpointExit !== basePrototype.handleMobileBreakpointExit
    ) {
      DoctorFinder.Views.Base.addMobileBreakpointHandler(
        this.handleMobileBreakpointEnter.bind(this),
        this.handleMobileBreakpointExit.bind(this),
      );
    }

    // Listen for tooltip element interactions and JIT tooltips onto those elements
    this.tooltips = {
      ...this.tooltips,
    };
    return jQuery.each(this.tooltips, (selector, options) =>
      this.$el.on("focus mouseenter", selector, (event) => Sparkle.Tooltips.add(event.currentTarget, null, options)),
    );
  }

  // EVENT HANDLERS

  handleMobileBreakpointEnter() {}

  handleMobileBreakpointExit() {}

  // RENDERING

  render() {
    if (this.template !== false) {
      this.replaceContents(this.$el, this.renderTemplate(this.contextForRender()));
    }
    this.rendered = true;
    Sparkle.Events.contentUpdated(this.el);

    return this;
  }

  rerender() {
    if (this.rendered) {
      this.render();
    }

    return this;
  }

  renderTemplate(context) {
    const path = `physician_finder/templates/${this.template}`;
    this.assert.presence(window.JST, "JST is not loaded");
    const template = this.assert.presence(Sparkle.Client.jstTemplate(path), `JST Template not found: ${path}`);

    // Single chokepoint for every view template: guarantee an I18n instance even
    // when the view supplied a custom context that omitted it (contextForRender is
    // bypassed by views that build their own context). Without this a template
    // calling I18n.t throws ("I18n is not defined") and aborts the finder render.
    context ??= {};
    context.I18n ??= this.i18nForRender();

    try {
      return template(context);
    } catch (error) {
      return this.assert.failAssertion(`DoctorFinder failed to render template '${template}': ${error.message}`);
    }
  }

  contextForRender() {
    const context = {};
    // Defaults are included in case a model attribute has been set to `undefined`, which would cause the key not to appear
    // in the context sent to the template.
    jQuery.extend(
      context,
      typeof this.model?.defaults === "function" ? this.model.defaults() : undefined,
      this.model != null ? this.model.toJSON() : undefined,
    );
    context["I18n"] = this.i18nForRender();
    context["renderPartial"] = this.renderPartial;
    if (this.collection != null) {
      context["collection"] = this.collection;
    }
    // JST templates were compiled against legacy EJS where `obj` was bound to the
    // full locals object inside `with (locals || {})`. The current JST compiler
    // no longer injects `var obj = locals;`, so templates that reference bare
    // `obj` (e.g. `{ doctor: obj }`) throw ReferenceError. Re-expose it here so
    // all templates keep working without touching the .jst.ejs files.
    context["obj"] = context;
    return context;
  }

  // Resolves the I18n instance to hand to a template. Falls back to the
  // finder-wide localized catalog (set in the DoctorFinder constructor) when this
  // view's own I18n was never threaded through the constructor chain — without
  // this, a template that calls I18n.t would receive an undefined I18n and throw,
  // aborting the finder's render.
  i18nForRender() {
    if (this.I18n?.backend != null && !_.isEmpty(this.I18n.backend)) {
      return this.I18n;
    }
    return DoctorFinder.Views.Base.fallbackI18n || this.I18n;
  }

  // HELPER METHODS

  getStickyHeaderOffset() {
    if (_.isFunction(this.options.stickyHeaderOffset)) {
      return this.options.stickyHeaderOffset() || 0;
    } else {
      return this.options.stickyHeaderOffset || 0;
    }
  }

  scrollToTop(options = {}) {
    options = _.defaults(options, { element: this.el, offset: 0, delay: 200, onlyScrollUp: false });
    const scrollTarget = jQuery(options.element).offset().top + options.offset;
    if (options.onlyScrollUp && scrollTarget >= jQuery(window).scrollTop()) {
      return;
    }
    return jQuery("html, body").animate({ scrollTop: scrollTarget }, options.delay);
  }

  isMobileSize() {
    return DoctorFinder.Views.Base.isMobileSize;
  }

  // PRIVATE

  eachAncestor(fn) {
    let proto = Object.getPrototypeOf(this.constructor.prototype);
    const result = [];
    while (proto && proto.constructor !== DoctorFinder.Views.Base && proto.constructor !== Backbone.View) {
      result.push(fn(proto));
      proto = Object.getPrototypeOf(proto);
    }
    if (proto && proto.constructor === DoctorFinder.Views.Base) {
      result.push(fn(proto));
    }
    return result;
  }

  renderPartial(name, locals) {
    const context = { I18n: this.i18nForRender(), renderPartial: this.renderPartial };
    _.keys(locals).forEach((key) => (context[key] = locals[key])); // Custom extend that ensures even incoming keys with `undefined` values still appear as local variables. Neither jQuery and Underscore have method with this behaviour.
    return JST[name](context);
  }

  replaceContents(target, ...newContents) {
    target = typeof target === "string" ? this.$el.find(target) : jQuery(target);
    target.children().detach(); // Don't blow away event handlers on subviews
    target.empty(); // Remove any text nodes
    return newContents.forEach((content) => target.append(content));
  }

  // Listens for the event on target and re-triggers it on self
  proxyEvent(target, event) {
    return this.listenTo(target, event, (...payload) => this.triggerEvent(event, ...Array.from(payload)));
  }

  // Used to communicate UI interaction events up to parent views
  triggerEvent(event, ...payload) {
    this.$el.trigger(event, payload);
    return this.trigger(event, ...Array.from(payload));
  }
};
